import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import Tasks from 'APP/Tasks';
import { ChatBackgroundPattern, ChatBackgroundType } from 'APP/constants/appearance';
import { IClientConfigBackground } from 'APP/model/auth/authModel.types';
import { DEFAULT_CHAT_BACKGROUND } from 'APP/store/Appearance/Appearance.contants';
import Entities from 'STORE';
import {
  IChatBackgroundCustomItem,
  ICustomChatBackground,
  IPatternChatBackground,
  IWallpaperChatBackground,
  TChatBackground,
} from 'STORE/Appearance/Appearance.types';
import { ClientConfigStoreKey } from 'STORE/ClientConfig/ClientConfig';

interface TChatBackgroundSelectionPresenter {
  patterns: ChatBackgroundPattern[];
  activeIndex: number | null;
  wallpapers: IClientConfigBackground[];
  file: File | null;
  activeChatBackground: TChatBackground;
  customBackgrounds: IChatBackgroundCustomItem[];
  backgrounds: TChatBackground[];
  hasUploadItem: boolean;
  setActiveChatBackground(background: TChatBackground): void;
  onSelectFile(file: File): void;
  onUploadSuccess(url: string): void;
  onCancelUpload(): void;
  onDeleteCustomBackground(id: string): void;
}

interface TChatBackgroundSelectionPresenterParams {
  activeChatBackground: TChatBackground;
  onChatBackgroundChange(chatBackground: TChatBackground): void;
}

export function useChatBackgroundSelectionPresenter({
  activeChatBackground,
  onChatBackgroundChange,
}: TChatBackgroundSelectionPresenterParams): TChatBackgroundSelectionPresenter {
  const presenter = useLocalObservable<TChatBackgroundSelectionPresenter>(() => ({
    patterns: Object.values(ChatBackgroundPattern),
    file: null,
    activeChatBackground: activeChatBackground,

    get wallpapers(): IClientConfigBackground[] {
      return Entities.clientConfig.getConfig(ClientConfigStoreKey.Backgrounds) || [];
    },

    get hasUploadItem(): boolean {
      return presenter.file !== null;
    },

    get activeIndex(): number | null {
      let index = presenter.backgrounds.findIndex(
        (background) => background.background === activeChatBackground.background
      );
      if (index !== -1) {
        index++;
        if (presenter.hasUploadItem) {
          index++;
        }
      }
      return index >= 0 ? index : null;
    },

    get customBackgrounds(): IChatBackgroundCustomItem[] {
      return Array.from(Entities.appearance.chatBackgroundsCustom);
    },

    get backgrounds(): TChatBackground[] {
      const customBackgrounds: ICustomChatBackground[] = presenter.customBackgrounds.map(
        (item) => ({
          type: ChatBackgroundType.Custom,
          background: item.id,
        })
      );

      const patternBackgrounds: IPatternChatBackground[] = presenter.patterns.map((pattern) => ({
        type: ChatBackgroundType.Pattern,
        background: pattern,
      }));
      const wallpaperBackgrounds: IWallpaperChatBackground[] = presenter.wallpapers.map(
        (wallpaper) => ({
          type: ChatBackgroundType.Wallpaper,
          background: wallpaper.id,
        })
      );

      return [...customBackgrounds, ...patternBackgrounds, ...wallpaperBackgrounds];
    },

    setActiveChatBackground(background: TChatBackground): void {
      presenter.activeChatBackground = background;
    },

    onSelectFile(file: File): void {
      presenter.file = file;
    },

    onUploadSuccess(url: string): void {
      const id = Tasks.appearance.setCustomChatBackground(url);
      if (id) {
        onChatBackgroundChange({ type: ChatBackgroundType.Custom, background: id });
      }
      presenter.file = null;
    },

    onCancelUpload(): void {
      presenter.file = null;
    },

    onDeleteCustomBackground(id: string): void {
      onChatBackgroundChange(DEFAULT_CHAT_BACKGROUND);
      Tasks.appearance.deleteCustomChatBackground(id);
    },
  }));

  useEffect(() => {
    presenter.setActiveChatBackground(activeChatBackground);
  }, [activeChatBackground]);

  return presenter;
}
